import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, Search, Download } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { fetchSports } from "@/lib/sportsCatalog";
import DiscussionListItem from "@/components/discussions/DiscussionListItem";
import DiscussionDetail from "@/components/discussions/DiscussionDetail";
import { MILESTONES } from "@/components/discussions/discussionsShared";
import { useLang, translateFr as tfr } from "@/i18n";
import StatusTabs from "@/components/prospecting/StatusTabs";
import WorkspaceBreadcrumb from "@/components/prospecting/WorkspaceBreadcrumb";

const TABS = [
  { id: "all", label: tfr("Toutes") },
  { id: "active", label: "Actives" },
  { id: "watch", label: tfr("À surveiller") },
  { id: "blocked", label: tfr("Bloquées") },
];

export default function DiscussionsActives() {
  const { tf } = useLang();
  const navigate = useNavigate();
  const { discussionId } = useParams();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, active: 0, watch: 0, blocked: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [sport, setSport] = useState("__all__");
  const [milestone, setMilestone] = useState("__all__");
  const [sports, setSports] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchSports().then(setSports).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ tab, q, sport, milestone });
      const r = await api.get(`/discussions?${params}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch {
      toast.error(tf("Impossible de charger les discussions"));
    } finally { setLoading(false); }
  }, [tab, q, sport, milestone, tf]);

  useEffect(() => { load(); }, [load]);

  // deep-link from Pipeline / athlete profile: /discussions-actives?athlete=:pid
  const athleteParam = searchParams.get("athlete");
  useEffect(() => {
    if (!athleteParam || discussionId) return;
    api.get(`/discussions?player_id=${athleteParam}`).then((r) => {
      const first = (r.data.items || [])[0];
      if (first) navigate(`/discussions-actives/${first.id}`, { replace: true });
    }).catch(() => {});
  }, [athleteParam, discussionId, navigate]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const r = await api.get(`/discussions/${id}`);
      setDetail(r.data);
    } catch {
      toast.error(tf("Discussion introuvable"));
      navigate("/discussions-actives", { replace: true });
    } finally { setDetailLoading(false); }
  }, [navigate, tf]);

  useEffect(() => {
    if (discussionId) loadDetail(discussionId);
    else setDetail(null);
  }, [discussionId, loadDetail]);

  // desktop auto-select first item
  useEffect(() => {
    if (!discussionId && !athleteParam && items.length > 0 && window.innerWidth >= 1024) {
      navigate(`/discussions-actives/${items[0].id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, discussionId]);

  const refreshAll = useCallback(() => {
    load();
    if (discussionId) loadDetail(discussionId);
  }, [load, loadDetail, discussionId]);

  const exportCsv = async () => {
    if (exporting || items.length === 0) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ tab, q, sport, milestone });
      const r = await api.get(`/discussions/export.csv?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = url;
      el.download = `athlynx-discussions-${new Date().toISOString().slice(0, 10)}.csv`;
      el.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(tf("Export impossible"));
    } finally { setExporting(false); }
  };

  const sportsUsed = useMemo(() => {
    const ids = new Set(items.map((i) => i.athlete?.sport).filter(Boolean));
    return sports.filter((s) => ids.has(s.id));
  }, [sports, items]);

  const showDetailMobile = !!discussionId;

  return (
    <div className="px-4 md:px-6 lg:px-8 py-5 max-w-[1500px] mx-auto animate-fade-in" data-testid="discussions-page">
      <WorkspaceBreadcrumb currentLabel="Discussions actives" testid="discussions-back-dashboard" />
      <div className="flex items-start justify-between gap-3 mt-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-[34px] font-bold tracking-tight" data-testid="discussions-title">{tf("Discussions actives")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{tf("Suivez vos discussions en cours et identifiez ce qui permet de faire avancer chaque dossier vers un mandat.")}</p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={exporting || items.length === 0}
          className="h-10 gap-1.5 border-border dark:border-white/10" data-testid="discussions-export-btn">
          <Download className="h-4 w-4" /> <span className="hidden sm:inline">{tf("Exporter")}</span>
        </Button>
      </div>

      {/* Tabs + filters */}
      <div className={cn("mt-4 space-y-3", showDetailMobile && "hidden lg:block")}>
        <StatusTabs containerTestId="discussions-tabs" value={tab} onChange={setTab}
          tabs={TABS.map((t) => ({
            id: t.id, label: tf(t.label), count: counts[t.id] ?? 0,
            tone: { active: "info", watch: "warning", blocked: "danger" }[t.id] || "neutral",
            testid: `discussions-tab-${t.id}`, countTestid: `discussions-count-${t.id}`,
          }))} />
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tf("Rechercher un athlète, un club, un décideur…")}
              className="pl-9 h-10 rounded-xl border-border bg-card text-sm" data-testid="discussions-search" />
          </div>
          <div className="flex gap-2">
            <Select value={sport} onValueChange={setSport}>
              <SelectTrigger className="h-10 w-[140px] rounded-xl border-border bg-card" data-testid="discussions-sport-filter">
                <SelectValue placeholder={tf("Tous les sports")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{tf("Tous les sports")}</SelectItem>
                {sportsUsed.map((s) => <SelectItem key={s.id} value={s.id}>{tf(s.label)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={milestone} onValueChange={setMilestone}>
              <SelectTrigger className="h-10 w-[160px] rounded-xl border-border bg-card" data-testid="discussions-milestone-filter">
                <SelectValue placeholder={tf("Toutes les étapes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{tf("Toutes les étapes")}</SelectItem>
                {MILESTONES.map((m) => <SelectItem key={m.id} value={m.id}>{tf(m.label)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Master / detail */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(280px,0.35fr)_minmax(0,1fr)] gap-4 items-start">
        {/* List */}
        <div className={cn("space-y-2 min-w-0", showDetailMobile && "hidden lg:block")} data-testid="discussions-list">
          {loading ? (
            [0, 1, 2].map((i) => <div key={i} className="h-[104px] rounded-2xl bg-secondary/60 animate-pulse" />)
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card px-5 py-10 text-center" data-testid="discussions-empty">
              <p className="text-sm font-medium">{tf("Aucune discussion dans cette vue.")}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {tf("Les athlètes passés « En discussion » dans la Pipeline apparaîtront ici.")}
              </p>
              <Button asChild variant="outline" size="sm" className="mt-4 h-9 border-border dark:border-white/10">
                <Link to="/pipeline" data-testid="discussions-empty-pipeline-cta">{tf("Voir la Pipeline")}</Link>
              </Button>
            </div>
          ) : (
            items.map((it) => (
              <DiscussionListItem key={it.id} item={it} selected={it.id === discussionId}
                onClick={() => navigate(`/discussions-actives/${it.id}`)} />
            ))
          )}
        </div>

        {/* Detail */}
        <div className={cn("min-w-0", !showDetailMobile && "hidden lg:block")}>
          {showDetailMobile && (
            <button onClick={() => navigate("/discussions-actives")}
              className="lg:hidden inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3"
              data-testid="discussion-back-list">
              <ChevronLeft className="h-3.5 w-3.5" /> {tf("Discussions actives")}
            </button>
          )}
          {detailLoading ? (
            <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
              <div className="h-14 w-2/3 rounded-xl bg-secondary/60 animate-pulse" />
              <div className="h-24 rounded-xl bg-secondary/60 animate-pulse" />
              <div className="h-64 rounded-xl bg-secondary/60 animate-pulse" />
            </div>
          ) : detail ? (
            <DiscussionDetail detail={detail} onRefresh={refreshAll} />
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-card/50 px-6 py-16 text-center text-sm text-muted-foreground"
              data-testid="discussion-detail-empty">
              {tf("Sélectionnez un athlète pour ouvrir l'espace de discussion.")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
